import { useTranslation } from "react-i18next";
import { useLangFont } from "../../../hooks/useLangFont";

const MAIL_TO = "zoshinjp@worldlink.jp";

const channelKeys = [
  {
    icon: "ri-chat-smile-2-line",
    nameKey: "contact_ch_0_name",
    targetKey: "contact_ch_0_target",
    descKey: "contact_ch_0_desc",
    iconBg: "bg-[#06C755]/10",
    iconColor: "text-[#06C755]",
    qr: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/a30d475fe537245c8258270cd6281102.png",
    email: null,
    addFriendUrl: "https://line.me/ti/p/@219mosmz",
    addFriendColor: "bg-[#06C755] hover:bg-[#05b34c]",
    addFriendIcon: "ri-chat-smile-2-line",
    addFriendKey: "contact_line_add_btn",
  },
  {
    icon: "ri-wechat-line",
    nameKey: "contact_ch_1_name",
    targetKey: "contact_ch_1_target",
    descKey: "contact_ch_1_desc",
    iconBg: "bg-[#2aae67]/10",
    iconColor: "text-[#2aae67]",
    qr: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/e52510f6195ea590e26e49fdc56eb2b2.png",
    email: null,
    addFriendUrl: "https://work.weixin.qq.com/ca/cawcdeaba961a86569",
    addFriendColor: "bg-[#2aae67] hover:bg-[#229058]",
    addFriendIcon: "ri-wechat-line",
    addFriendKey: "contact_wecom_add_btn",
  },
  {
    icon: "ri-mail-send-line",
    nameKey: "contact_ch_2_name",
    targetKey: "contact_ch_2_target",
    descKey: "contact_ch_2_desc",
    iconBg: "bg-[#1A2332]/10",
    iconColor: "text-[#C9A84C]",
    qr: null,
    email: MAIL_TO,
    addFriendUrl: null,
    addFriendColor: null,
    addFriendIcon: null,
    addFriendKey: null,
  },
];

const templateKeys = [
  "contact_mail_tmpl_0",
  "contact_mail_tmpl_1",
  "contact_mail_tmpl_2",
  "contact_mail_tmpl_3",
  "contact_mail_tmpl_4",
];

export default function Contact() {
  const { t } = useTranslation();
  const { heading, body } = useLangFont();

  const mailSubject = encodeURIComponent(t("contact_mail_subject"));
  const mailBody = encodeURIComponent(t("contact_mail_body"));
  const mailHref = `mailto:${MAIL_TO}?subject=${mailSubject}&body=${mailBody}`;

  return (
    <section id="contact" className="py-28 bg-white w-full">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-5">
          <span
            className="inline-block bg-[#1A2332] text-white text-xs font-bold tracking-[0.3em] uppercase px-4 py-2 rounded-full mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {t("contact_label")}
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#1A2332] mb-3 tracking-tight"
            style={{ fontFamily: heading }}
          >
            {t("contact_title")}
          </h2>
          <p
            className="text-[#C9A84C] text-base font-medium tracking-wide"
            style={{ fontFamily: body }}
          >
            {t("contact_subtitle_1")}
          </p>
          <p
            className="text-gray-400 text-sm mt-2 tracking-wider"
            style={{ fontFamily: body }}
          >
            {t("contact_subtitle_2")}
          </p>
        </div>

        {/* Channel Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 mb-16">
          {channelKeys.map((ch) => (
            <div
              key={ch.nameKey}
              className="bg-[#F8F7F4] rounded-2xl p-8 flex flex-col items-center text-center transition-all duration-300 hover:scale-[1.02]"
            >
              <div className={`w-20 h-20 rounded-full ${ch.iconBg} flex items-center justify-center mb-5`}>
                <i className={`${ch.icon} text-3xl ${ch.iconColor} w-8 h-8 flex items-center justify-center`} />
              </div>
              <h3
                className="text-xl font-bold text-[#1A2332] mb-1 tracking-tight"
                style={{ fontFamily: heading }}
              >
                {t(ch.nameKey)}
              </h3>
              <span
                className="text-gray-400 text-xs tracking-widest mb-4"
                style={{ fontFamily: body }}
              >
                {t(ch.targetKey)}
              </span>
              <p
                className="text-gray-500 text-sm leading-relaxed mb-5"
                style={{ fontFamily: body }}
              >
                {t(ch.descKey)}
              </p>

              {ch.qr && (
                <div className="mb-4 border border-gray-200 rounded-xl overflow-hidden w-28 h-28 flex-shrink-0">
                  <img src={ch.qr} alt={`QRコード`} className="w-full h-full object-cover" />
                </div>
              )}

              {ch.addFriendUrl && (
                <a
                  href={ch.addFriendUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full flex items-center justify-center gap-2 ${ch.addFriendColor} text-white text-sm font-bold py-3 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer mb-2`}
                  style={{ fontFamily: body }}
                >
                  <i className={`${ch.addFriendIcon} text-base`} />
                  {t(ch.addFriendKey!)}
                </a>
              )}

              {ch.email && (
                <div className="w-full flex flex-col gap-3 mt-auto">
                  <div className="flex items-center justify-center gap-2 bg-[#1A2332]/5 rounded-xl px-4 py-2.5">
                    <i className="ri-mail-line text-[#C9A84C] text-sm w-4 h-4 flex items-center justify-center" />
                    <span
                      className="text-[#1A2332] text-xs"
                      style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.04em" }}
                    >
                      {ch.email}
                    </span>
                  </div>
                  <a
                    href={mailHref}
                    className="btn-lift w-full flex items-center justify-center gap-2 bg-[#1A2332] hover:bg-[#243246] text-white text-sm font-bold py-3 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: body }}
                  >
                    <i className="ri-mail-send-line text-base" />
                    {t("contact_mail_cta_btn")}
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Logistics Notice */}
        <div
          className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-16"
          style={{ fontFamily: body }}
        >
          <span className="flex items-center gap-2">
            <i className="ri-truck-line text-[#C9A84C] w-4 h-4 flex items-center justify-center" />
            {t("contact_logistics_1")}
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-time-line text-[#C9A84C] w-4 h-4 flex items-center justify-center" />
            {t("contact_logistics_2")}
          </span>
        </div>

        {/* Email Detail Box */}
        <div className="max-w-3xl mx-auto bg-[#F8F7F4] rounded-3xl p-10">
          <div className="flex flex-col items-center text-center gap-5">
            <div className="w-16 h-16 rounded-full bg-[#1A2332] flex items-center justify-center">
              <i className="ri-mail-open-line text-[#C9A84C] text-2xl w-6 h-6 flex items-center justify-center" />
            </div>
            <div>
              <h3
                className="text-2xl font-bold text-[#1A2332] mb-2 tracking-tight"
                style={{ fontFamily: heading }}
              >
                {t("contact_mail_title")}
              </h3>
              <p
                className="text-gray-400 text-sm leading-relaxed"
                style={{ fontFamily: body }}
              >
                {t("contact_mail_desc")}
              </p>
            </div>

            <div className="flex items-center gap-3 bg-white border border-[#C9A84C]/20 rounded-2xl px-8 py-4 w-full justify-center">
              <i className="ri-mail-line text-[#C9A84C] text-xl w-5 h-5 flex items-center justify-center" />
              <span
                className="text-[#1A2332] font-semibold text-base tracking-wide"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {MAIL_TO}
              </span>
            </div>

            <div
              className="w-full bg-white rounded-2xl border border-gray-100 px-7 py-5 text-left"
              style={{ fontFamily: body }}
            >
              <p className="text-xs text-gray-400 mb-3 font-semibold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {t("contact_mail_tmpl_label")}
              </p>
              <ul className="flex flex-col gap-2">
                {templateKeys.map((key) => (
                  <li key={key} className="flex items-center gap-2 text-sm text-gray-500" style={{ fontFamily: body }}>
                    <i className="ri-checkbox-circle-line text-[#C9A84C] flex-shrink-0 w-4 h-4 flex items-center justify-center" />
                    {t(key)}
                  </li>
                ))}
              </ul>
            </div>

            <a
              href={mailHref}
              className="btn-lift w-full flex items-center justify-center gap-3 bg-[#1A2332] hover:bg-[#243246] text-white font-bold text-sm py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: body }}
            >
              <i className="ri-mail-send-line text-base" />
              {t("contact_mail_cta_btn")}
            </a>

            <p
              className="text-gray-400 text-xs"
              style={{ fontFamily: body }}
            >
              {t("contact_mail_after")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
